import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import { useAuth } from "../store";
import {
  Calendar,
  Clock,
  Mail,
  Plus,
  RefreshCcw,
  Users,
  Send,
} from "lucide-react";

const CHANNELS = [
  { value: "AUTO_FALLBACK", label: "Email + WhatsApp (auto)" },
  { value: "EMAIL_ONLY", label: "Email uniquement" },
  { value: "WHATSAPP_ONLY", label: "WhatsApp uniquement" },
];

const RECURRENCE = [
  { value: "DAILY", label: "Chaque jour" },
  { value: "WEEKLY", label: "Chaque semaine" },
  { value: "MONTHLY", label: "Chaque mois" },
  { value: "ONCE", label: "Une seule fois" },
];

const WEEK_DAYS = [
  { value: 1, label: "Lun" },
  { value: 2, label: "Mar" },
  { value: 3, label: "Mer" },
  { value: 4, label: "Jeu" },
  { value: 5, label: "Ven" },
  { value: 6, label: "Sam" },
  { value: 7, label: "Dim" },
];

function formatDateTime(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);
  return d.toLocaleString("fr-FR");
}

export default function ManualCampaigns() {
  const { user } = useAuth();
  const connected = !!user;

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [runs, setRuns] = useState({});

  const [customerSearch, setCustomerSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({
    activeOnly: true,
    type: "",
    hasEmail: false,
    hasPhone: false,
  });

  const [form, setForm] = useState({
    name: "",
    channelMode: "AUTO_FALLBACK",
    emailSubject: "",
    emailBody: "",
    whatsappMessage: "",
    scheduleType: "DAILY",
    scheduleTime: "08:00",
    scheduleDaysOfWeek: [1, 2, 3, 4, 5],
    scheduleDayOfMonth: 1,
    scheduleDate: "",
    monthlyPick: "",
  });

  useEffect(() => {
    if (!connected) return;
    loadCampaigns();
  }, [connected]);

  useEffect(() => {
    if (!showModal) return;
    const t = setTimeout(() => loadCustomers(), 200);
    return () => clearTimeout(t);
  }, [customerSearch, showModal, filters]);

  async function loadCampaigns() {
    setLoading(true);
    try {
      const data = await apiFetch("/api/manual-campaigns");
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadCustomers() {
    if (!connected) return;
    try {
      const params = new URLSearchParams();
      if (customerSearch) params.set("search", customerSearch.trim());
      params.set("page", "1");
      params.set("limit", "200");
      if (filters.activeOnly) params.set("active_only", "true");
      if (filters.hasEmail) params.set("has_email", "true");
      if (filters.hasPhone) params.set("has_phone", "true");
      if (filters.type) params.set("type", filters.type);
      const data = await apiFetch(`/api/customers?${params.toString()}`);
      const rows = Array.isArray(data) ? data : data?.rows || [];
      setCustomers(
        rows.map((r) => ({
          ...r,
          id: r.id ?? r.uid,
        }))
      );
    } catch {
      setCustomers([]);
    }
  }

  async function loadRuns(id) {
    try {
      const data = await apiFetch(`/api/manual-campaigns/${id}/runs`);
      setRuns((prev) => ({ ...prev, [id]: Array.isArray(data) ? data : [] }));
    } catch {
      setRuns((prev) => ({ ...prev, [id]: [] }));
    }
  }

  function resetForm() {
    setForm({
      name: "",
      channelMode: "AUTO_FALLBACK",
      emailSubject: "",
      emailBody: "",
      whatsappMessage: "",
      scheduleType: "DAILY",
      scheduleTime: "08:00",
      scheduleDaysOfWeek: [1, 2, 3, 4, 5],
      scheduleDayOfMonth: 1,
      scheduleDate: "",
      monthlyPick: "",
    });
    setSelected([]);
    setFilters({ activeOnly: true, type: "", hasEmail: false, hasPhone: false });
    setCustomerSearch("");
    setCustomers([]);
    setEditingId(null);
  }

  function openCreate() {
    resetForm();
    setShowModal(true);
  }

  async function openEdit(id) {
    try {
      const row = await apiFetch(`/api/manual-campaigns/${id}`);
      setEditingId(id);
      setForm({
        name: row.name || "",
        channelMode: row.channel_mode || "AUTO_FALLBACK",
        emailSubject: row.email_subject || "",
        emailBody: row.email_body || "",
        whatsappMessage: row.whatsapp_message || "",
        scheduleType: row.schedule_type || "DAILY",
        scheduleTime: row.schedule_time || "08:00",
        scheduleDaysOfWeek: row.schedule_days_of_week || [1, 2, 3, 4, 5],
        scheduleDayOfMonth: row.schedule_day_of_month || 1,
        scheduleDate: row.schedule_date ? String(row.schedule_date).slice(0, 10) : "",
        monthlyPick: row.schedule_day_of_month
          ? new Date().toISOString().slice(0, 7) + "-" + String(row.schedule_day_of_month).padStart(2, "0")
          : "",
      });
      setSelected(row.recipient_ids || []);
      setShowModal(true);
    } catch (e) {
      alert(e?.message || "Erreur chargement campagne");
    }
  }

  function toggleSelect(id) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleWeekDay(day) {
    setForm((prev) => ({
      ...prev,
      scheduleDaysOfWeek: prev.scheduleDaysOfWeek.includes(day)
        ? prev.scheduleDaysOfWeek.filter((d) => d !== day)
        : [...prev.scheduleDaysOfWeek, day],
    }));
  }

  const validationErrors = useMemo(() => {
    const errors = [];
    if (!form.name) errors.push("Nom requis");
    if (selected.length === 0) errors.push("Selectionne au moins 1 client");
    if (form.scheduleType === "ONCE" && !form.scheduleDate) errors.push("Date requise");
    if (form.scheduleType !== "ONCE" && !form.scheduleTime) errors.push("Heure requise");
    if (form.scheduleType === "WEEKLY" && form.scheduleDaysOfWeek.length === 0) errors.push("Choisir au moins 1 jour");
    if (form.scheduleType === "MONTHLY" && !form.scheduleDayOfMonth) errors.push("Jour du mois requis");

    if (form.channelMode === "EMAIL_ONLY") {
      if (!form.emailSubject || !form.emailBody) errors.push("Sujet et contenu email requis");
    } else if (form.channelMode === "WHATSAPP_ONLY") {
      if (!form.whatsappMessage) errors.push("Message WhatsApp requis");
    } else {
      if (!form.emailSubject || !form.emailBody || !form.whatsappMessage) errors.push("Email + WhatsApp requis");
    }
    return errors;
  }, [form, selected]);

  async function handleSave(e) {
    e.preventDefault();
    if (validationErrors.length) return;
    setSaving(true);
    try {
      const url = editingId ? `/api/manual-campaigns/${editingId}` : "/api/manual-campaigns";
      const method = editingId ? "PUT" : "POST";
      await apiFetch(url, {
        method,
        body: {
          name: form.name,
          channelMode: form.channelMode,
          emailSubject: form.emailSubject,
          emailBody: form.emailBody,
          whatsappMessage: form.whatsappMessage,
          scheduleType: form.scheduleType,
          scheduleTime: form.scheduleTime,
          scheduleDaysOfWeek: form.scheduleType === "WEEKLY" ? form.scheduleDaysOfWeek : [],
          scheduleDayOfMonth: form.scheduleType === "MONTHLY" ? form.scheduleDayOfMonth : null,
          scheduleDate: form.scheduleType === "ONCE" ? form.scheduleDate : null,
          recipientIds: selected,
        },
      });
      setShowModal(false);
      await loadCampaigns();
    } catch (e) {
      alert(e?.message || "Erreur creation campagne");
    } finally {
      setSaving(false);
    }
  }

  async function runNow(id) {
    setRunningId(id);
    try {
      await apiFetch(`/api/manual-campaigns/${id}/run-now`, { method: "POST" });
      await loadRuns(id);
    } catch (e) {
      alert(e?.message || "Erreur execution");
    } finally {
      setRunningId(null);
    }
  }

  async function removeCampaign(id) {
    if (!window.confirm("Supprimer cette campagne ?")) return;
    try {
      await apiFetch(`/api/manual-campaigns/${id}`, { method: "DELETE" });
      await loadCampaigns();
    } catch (e) {
      alert(e?.message || "Erreur suppression");
    }
  }

  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      if (filters.activeOnly && c.is_active === false) return false;
      if (filters.type && String(c.type || "").toLowerCase() !== String(filters.type).toLowerCase()) return false;
      if (filters.hasEmail && !c.email) return false;
      if (filters.hasPhone && !c.phone) return false;
      return true;
    });
  }, [customers, filters]);

  const distinctTypes = useMemo(() => {
    const set = new Set();
    customers.forEach((c) => c.type && set.add(c.type));
    return Array.from(set);
  }, [customers]);

  const selectAllFiltered = () => {
    const ids = filteredCustomers.map((c) => c.id ?? c.uid).filter(Boolean);
    setSelected((prev) => Array.from(new Set([...prev, ...ids])));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Mail className="h-4 w-4" />
                <span>Communication</span>
                <span className="text-slate-300">/</span>
                <span className="font-medium text-slate-900">Campagnes</span>
              </div>
              <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                Campagnes manuelles
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Selectionne tes clients, redige ton message et programme l’envoi.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={loadCampaigns}
                disabled={!connected}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCcw className="h-4 w-4" />
                Rafraichir
              </button>
              <button
                onClick={openCreate}
                disabled={!connected}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                Nouvelle campagne
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-6">
        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            Chargement...
          </div>
        ) : list.length === 0 ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
              <Users className="h-7 w-7 text-slate-600" />
            </div>
            <h3 className="mt-4 text-lg font-bold text-slate-900">
              Aucune campagne
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Cree ta premiere campagne et programme tes envois.
            </p>
            <button
              onClick={openCreate}
              disabled={!connected}
              className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              Nouvelle campagne
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {list.map((c) => (
              <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      Prochain envoi: {formatDateTime(c.next_run_at)}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Destinataires: {c.recipients_count || 0}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEdit(c.id)}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-900 hover:bg-slate-50"
                    >
                      Editer
                    </button>
                    <button
                      onClick={() => removeCampaign(c.id)}
                      className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                    >
                      Supprimer
                    </button>
                    <button
                      onClick={() => runNow(c.id)}
                      disabled={runningId === c.id}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-900 hover:bg-slate-50"
                    >
                      <Send className="h-3.5 w-3.5" />
                      {runningId === c.id ? "..." : "Envoyer maintenant"}
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                    <Clock className="h-3 w-3" /> {c.schedule_time || "—"}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                    <Calendar className="h-3 w-3" /> {c.schedule_type}
                  </span>
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => loadRuns(c.id)}
                    className="text-xs text-slate-600 underline"
                  >
                    Voir l’historique
                  </button>
                  {runs[c.id] && runs[c.id].length > 0 && (
                    <div className="mt-2 space-y-1 text-xs text-slate-600">
                      {runs[c.id].slice(0, 5).map((r) => (
                        <div key={r.id}>
                          {formatDateTime(r.started_at)} · {r.run_reason} · {r.status} · {r.total_recipients} dest.
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <div className="text-lg font-bold text-slate-900">Nouvelle campagne</div>
                <div className="text-xs text-slate-500">Fuseau: Africa/Ouagadougou</div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
              >
                Fermer
              </button>
            </div>

            <form onSubmit={handleSave} className="max-h-[80vh] overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold text-slate-900">Nom *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                    placeholder="Ex: Relance paiement janvier"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-900">Canal</label>
                  <select
                    value={form.channelMode}
                    onChange={(e) => setForm({ ...form, channelMode: e.target.value })}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  >
                    {CHANNELS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold text-slate-900">Sujet email</label>
                  <input
                    value={form.emailSubject}
                    onChange={(e) => setForm({ ...form, emailSubject: e.target.value })}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                    placeholder="Objet"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-900">Message WhatsApp</label>
                  <textarea
                    rows={3}
                    value={form.whatsappMessage}
                    onChange={(e) => setForm({ ...form, whatsappMessage: e.target.value })}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                    placeholder="Message court WhatsApp"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-900">Contenu email</label>
                <textarea
                  rows={5}
                  value={form.emailBody}
                  onChange={(e) => setForm({ ...form, emailBody: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  placeholder="Contenu email (HTML ou texte simple)"
                />
                <div className="mt-1 text-xs text-slate-500">
                  Variables disponibles: {"{{customer_name}}"}, {"{{customer_email}}"}, {"{{customer_phone}}"}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="text-sm font-semibold text-slate-900">Recurrence</label>
                  <select
                    value={form.scheduleType}
                    onChange={(e) => setForm({ ...form, scheduleType: e.target.value })}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  >
                    {RECURRENCE.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-900">Heure</label>
                  <input
                    type="time"
                    value={form.scheduleTime}
                    onChange={(e) => setForm({ ...form, scheduleTime: e.target.value })}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-900">Date unique</label>
                  <input
                    type="date"
                    value={form.scheduleDate}
                    onChange={(e) => setForm({ ...form, scheduleDate: e.target.value })}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  />
                </div>
              </div>

              {form.scheduleType === "WEEKLY" && (
                <div>
                  <label className="text-sm font-semibold text-slate-900">Jours de la semaine</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {WEEK_DAYS.map((d) => (
                      <button
                        type="button"
                        key={d.value}
                        onClick={() => toggleWeekDay(d.value)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                          form.scheduleDaysOfWeek.includes(d.value)
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-700 border-slate-200"
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {form.scheduleType === "MONTHLY" && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <label className="text-sm font-semibold text-slate-900">Jour du mois</label>
                  <input
                    type="date"
                    value={form.monthlyPick}
                    onChange={(e) => {
                      const v = e.target.value;
                      const day = v ? new Date(v).getDate() : 1;
                      setForm({ ...form, monthlyPick: v, scheduleDayOfMonth: day });
                    }}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  />
                  <div className="mt-2 text-xs text-slate-600">
                    Le jour selectionne sera utilise chaque mois (dernier jour si invalide).
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">Destinataires</div>
                  <div className="text-xs text-slate-600">{selected.length} selectionnes</div>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={filters.activeOnly}
                      onChange={(e) => setFilters({ ...filters, activeOnly: e.target.checked })}
                    />
                    Actifs uniquement
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={filters.hasEmail}
                      onChange={(e) => setFilters({ ...filters, hasEmail: e.target.checked })}
                    />
                    Email dispo
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={filters.hasPhone}
                      onChange={(e) => setFilters({ ...filters, hasPhone: e.target.checked })}
                    />
                    Telephone dispo
                  </label>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs"
                  >
                    <option value="">Type client</option>
                    {distinctTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <input
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="Rechercher un client..."
                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={selectAllFiltered}
                    className="text-xs text-slate-600 underline"
                  >
                    Selectionner tous les clients filtres
                  </button>
                </div>
                <div className="mt-3 max-h-56 overflow-y-auto space-y-2">
                  {filteredCustomers.map((c) => {
                    const cid = c.id ?? c.uid;
                    return (
                    <label key={cid} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={selected.includes(cid)}
                        onChange={() => toggleSelect(cid)}
                      />
                      <span>{c.name}</span>
                      <span className="text-xs text-slate-400">{c.email || c.phone || ""}</span>
                    </label>
                  )})}
                  {filteredCustomers.length === 0 && (
                    <div className="text-xs text-slate-500">Aucun client</div>
                  )}
                </div>
              </div>

              {validationErrors.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  {validationErrors.map((e) => (
                    <div key={e}>• {e}</div>
                  ))}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving || validationErrors.length > 0}
                  className="flex-1 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-70"
                >
                  {saving ? "..." : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
